import React, { useEffect, useMemo } from 'react';
import { FormikProps } from 'formik';
import { getFormatTemplatePreview } from '../../../lib/services/format-template.service';
import { ISuggestedTemplate } from '../../../lib/types/format-template.types';
import { UseTemplateModal } from './use-template-modal.component';
import { IUseFormatTemplateFields } from './use-template-modal.types';

interface IUseTemplateModalGraphQLProps {
  open: boolean;
  onClose: () => void;
  templates: IFormatTemplate[];
  templatesLoading: boolean;
  ageGroupId?: string;
  ageGroupBucket: string | null;
  recommended: ISuggestedTemplate | null;
  form: FormikProps<IUseFormatTemplateFields>;
}

// Runs the server preview for the selected template — the only way to resolve a CATALOG
// template — to feed the stage-count guard and the roster/session counts. Stages never render.
export const UseTemplateModalGraphQL: React.FC<IUseTemplateModalGraphQLProps> = ({
  open,
  onClose,
  templates,
  templatesLoading,
  ageGroupId,
  ageGroupBucket,
  recommended,
  form,
}) => {
  const { values, setFieldValue } = form;

  const selectedTemplate = useMemo(
    () => templates.find(t => t._id === values.templateId) ?? null,
    [templates, values.templateId],
  );

  const { preview, loading: previewLoading, error: previewError } = getFormatTemplatePreview({
    templateId: values.templateId,
    ageGroupId,
    bandKey: values.bandKey,
  });

  useEffect(() => {
    if (previewError) console.error('formatTemplatePreview failed:', previewError);
  }, [previewError]);

  // Null until the preview settles, so an in-flight selection never reads as "no stages".
  const resolvedStageCount = preview && !previewLoading ? preview.stages.length : null;
  useEffect(() => {
    if (values.previewStageCount !== resolvedStageCount) setFieldValue('previewStageCount', resolvedStageCount);
  }, [resolvedStageCount, values.previewStageCount, setFieldValue]);

  return (
    <UseTemplateModal
      open={open}
      onClose={onClose}
      templates={templates}
      templatesLoading={templatesLoading}
      selectedTemplate={selectedTemplate}
      ageGroupBucket={ageGroupBucket}
      recommended={recommended}
      preview={preview}
      previewError={!!previewError}
      form={form}
    />
  );
};
